import type { VellumAuthMeResponse } from "@vellum/vellum-api";
import { createContext, useContext } from "react";

export type AuthContextValue = {
  user: VellumAuthMeResponse | null;
  loading: boolean;
  refresh: () => Promise<void>;
  signOut: () => Promise<void>;
};

// The context lives in its own component-free module on purpose. React Fast
// Refresh only treats a module as a refresh boundary when every export is a
// component, so keeping `createContext` out of `AuthProvider.tsx` means editing
// the provider hot-swaps it without minting a new context object. Otherwise the
// mounted provider (from a `main.tsx` that never re-executes) keeps handing out
// the old context while freshly refreshed consumers read the new one, and every
// `useAuth` call throws until a full reload.
export const AuthContext = createContext<AuthContextValue | null>(null);

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
}

/**
 * Like `useAuth`, but returns `null` instead of throwing when no `AuthProvider`
 * is mounted. For shared components that render in apps which don't use Vellum
 * auth — Customer Platform has its own session and no provider.
 */
export function useOptionalAuth() {
  return useContext(AuthContext);
}
